import { useEffect, useState, type ReactElement } from 'react';
import { GRID_END_MINUTE, GRID_START_MINUTE } from '@boud/contracts';
import { minutesToY } from '../../lib/calendar';
import { todayInBuilding } from '../../lib/calendar';

/** Re-drawn once a minute. The line moves ~0.8px in that time at the grid's scale. */
const TICK_MS = 60_000;

/**
 * The "you are here" line across a day column.
 *
 * A calendar grid is a wall of identical half-hour rows, and reading a position
 * off it means counting from the hour labels at the edge. This is the one mark
 * that answers "is that meeting next, or did I miss it?" without counting — and
 * it is why every calendar people already use has one.
 *
 * ## What it deliberately does not do
 *
 * It renders **only on today**, and only when now is inside the booking grid.
 * A line drawn on Thursday's column while you are looking at next Thursday
 * would be worse than none: it means something different from what it looks
 * like, and nothing on screen says so.
 *
 * The clock is the *building's*, via `todayInBuilding` and the same
 * `minutesToY` every event uses. Reading the browser's local midnight instead
 * would put the line hours out for anybody travelling, which is the case where
 * they would most want to trust it.
 */
export function NowLine({ day }: { day: string }): ReactElement | null {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    /*
     * Aligned to the next whole minute rather than ticking every 60s from
     * mount, so the line moves when the clock does. Two grids mounted a few
     * seconds apart would otherwise update at different moments, which is
     * visible when both are on screen.
     */
    const msToNextMinute = 60_000 - (Date.now() % 60_000);

    let interval: ReturnType<typeof setInterval> | undefined;
    const timeout = setTimeout(() => {
      setNow(new Date());
      interval = setInterval(() => setNow(new Date()), TICK_MS);
    }, msToNextMinute);

    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, []);

  // Not today's column. Nothing to say about it.
  if (day !== todayInBuilding()) return null;

  /*
   * The wall-clock minute in the building, not in the viewer's timezone —
   * `en-GB` with an explicit zone, so a laptop still on European time draws the
   * line where Riyadh actually is.
   */
  const [hour, minute] = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Riyadh',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
    .format(now)
    .split(':')
    .map(Number);

  const minutes = (hour ?? 0) * 60 + (minute ?? 0);

  // Outside the bookable day — before it opens or after it closes. Drawing the
  // line pinned to an edge would imply a time that is not the time.
  if (minutes < GRID_START_MINUTE || minutes > GRID_END_MINUTE) return null;

  return (
    <div
      className="cal-now"
      style={{ top: minutesToY(minutes) }}
      /*
       * Decoration. The time is already on the clock, in the page, and read out
       * by the operating system — announcing it here would interrupt somebody
       * reading the schedule, once a minute, forever.
       */
      aria-hidden="true"
    >
      <span className="cal-now-dot" />
    </div>
  );
}
